import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Chip, Grid
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import AppToast from "../../components/AppToast";
import ConfirmDialog from "../../components/ui/ConfirmDialog";

const ManageFIRMappings = () => {
  const { token, userData } = useAuth();
  const isEngineer = userData?.role === "ENGINEER";
  const navigate = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, mapping: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, mapping: null });
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const [formData, setFormData] = useState({ propertyType: "", componentsStr: "" });
  const [saving, setSaving] = useState(false);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/fir/mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setMappings(res.data.data);
      }
    } catch (error) {
      setToast({ open: true, type: "error", title: "Error", message: "Failed to fetch FIR mappings" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMappings();
  }, [token]);

  const handleOpenModal = (mapping = null) => {
    if (mapping) {
      setFormData({ propertyType: mapping.propertyType, componentsStr: mapping.components.map(c => c.title || c).join(", ") });
    } else {
      setFormData({ propertyType: "", componentsStr: "" });
    }
    setModalState({ open: true, mapping });
  };

  const handleSave = async () => {
    if (!formData.propertyType) {
      setToast({ open: true, type: "error", title: "Error", message: "Property Type is required" });
      return;
    }

    const components = formData.componentsStr ? formData.componentsStr.split(",").map(s => s.trim()).filter(Boolean).map(title => ({ title })) : [];

    try {
      setSaving(true);
      const res = await axios.post(`${url}/api/fir/mappings`, {
        propertyType: formData.propertyType,
        components
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setToast({ open: true, type: "success", title: "Success", message: "FIR Mapping saved successfully" });
        setModalState({ open: false, mapping: null });
        fetchMappings();
      }
    } catch (error) {
      setToast({ open: true, type: "error", title: "Error", message: error.response?.data?.message || "Failed to save mapping" });
    } finally {
      setSaving(false);
    }
  };

  const executeDelete = async () => {
    if (!deleteConfirm.mapping) return;
    try {
      setSaving(true);
      const res = await axios.delete(`${url}/api/fir/mappings/${deleteConfirm.mapping._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setToast({ open: true, type: "success", title: "Success", message: "Mapping deleted successfully" });
        setDeleteConfirm({ open: false, mapping: null });
        fetchMappings();
      }
    } catch (error) {
      setToast({ open: true, type: "error", title: "Error", message: error.response?.data?.message || "Failed to delete mapping" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>Manage FIR Mappings</Typography>
        {!isEngineer && (
          <Button variant="contained" onClick={() => handleOpenModal()} sx={{ backgroundColor: COLORS.PRIMARY, "&:hover": { backgroundColor: COLORS.SECONDARY } }}>
            + Add New Mapping
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: `${COLORS.PRIMARY}10` }}>
                <TableCell align="center" sx={{ width: '33.33%' }}><Typography sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>Property Type</Typography></TableCell>
                <TableCell align="center" sx={{ width: '33.33%' }}><Typography sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>Components Configured</Typography></TableCell>
                <TableCell align="center" sx={{ width: '33.33%' }}><Typography sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
              ) : mappings.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}>No mappings found</TableCell></TableRow>
              ) : (
                mappings.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell align="center"><Typography>{row.propertyType}</Typography></TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                        {row.components.map((c, i) => <Chip key={i} label={c.title || c} size="small" sx={{ backgroundColor: `${COLORS.SECONDARY}20`, color: COLORS.SECONDARY }} />)}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => navigate(`/admin/fir-mappings/${row._id}`)} sx={{ color: COLORS.SECONDARY }}><Icon name={isEngineer ? "eye" : "edit"} size={20} /></IconButton>
                      {!isEngineer && (
                        <IconButton onClick={() => setDeleteConfirm({ open: true, mapping: row })} sx={{ color: COLORS.ERROR }}><Icon name="delete" size={20} /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalState.open} onClose={() => setModalState({ open: false, mapping: null })} fullWidth maxWidth="sm">
        <DialogTitle>{modalState.mapping ? "Edit FIR Mapping" : "Add FIR Mapping"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography sx={{ mb: 1, fontSize: 14, color: COLORS.TEXT }}>
                Enter a name for the new property type (e.g., Studio, 1 BHK).
              </Typography>
              <TextField
                fullWidth
                label="Property Type"
                value={formData.propertyType}
                onChange={(e) => setFormData(p => ({ ...p, propertyType: e.target.value }))}
                disabled={!!modalState.mapping}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalState({ open: false, mapping: null })}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ backgroundColor: COLORS.PRIMARY, "&:hover": { backgroundColor: COLORS.SECONDARY } }}>
            {saving ? <CircularProgress size={24} /> : "Save Mapping"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete FIR Mapping"
        message={`Are you sure you want to delete the "${deleteConfirm.mapping?.propertyType}" FIR mapping? This action cannot be undone.`}
        onConfirm={executeDelete}
        onCancel={() => setDeleteConfirm({ open: false, mapping: null })}
        confirmText="Delete"
      />

      <AppToast open={toast.open} onClose={() => setToast(p => ({ ...p, open: false }))} type={toast.type} title={toast.title} message={toast.message} />
    </Box>
  );
};

export default ManageFIRMappings;
